import { CartButton } from '@components/cartbutton';
import { CartButtonV5 } from '@components/cartbutton/v5';
import { Star } from '@components/star';
import { TCartItem, TStore } from '@components/types';
import { getCombinedName } from '@components/variant/variantSelector';
import {
    getSafeUrl,
    humanizeCurrency,
    PriceCalculation,
    sanityIoImageLoader,
} from '@core/utils';
import { ImageOff, Trash2 } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import React, { useState } from 'react';

interface Props {
    cartItem: TCartItem;
    noLink?: boolean;
    store?: TStore;
    sellerStore?: TStore;
    onRemove: (cartItem: TCartItem) => void;
}

const hasProductImage = (cartItem: TCartItem) =>
    (cartItem?.product?.images || []).some((img) => (img?.url || '').length > 0);

const ProductThumb: React.FC<{ cartItem: TCartItem }> = ({ cartItem }) => {
    const [failed, setFailed] = useState(false);
    const imageAvailable = hasProductImage(cartItem) && !failed;

    if (!imageAvailable) {
        return (
            <div
                className="box-border w-16 h-16 rounded-xl bg-neutral-100 border border-neutral-200/70 flex items-center justify-center flex-shrink-0"
                title="No image available"
            >
                <ImageOff className="w-6 h-6 text-neutral-400" strokeWidth={1.5} />
            </div>
        );
    }

    return (
        <Image
            loader={sanityIoImageLoader}
            src={cartItem.product?.images?.[0]?.url}
            alt={cartItem.product?.name || 'Product'}
            width="64"
            height="64"
            onError={() => setFailed(true)}
            className="w-16 h-16 rounded-xl aspect-square object-cover flex-shrink-0"
        />
    );
};

export const CartItemCard: React.FC<Props> = ({ cartItem, noLink = false, store, sellerStore, onRemove }) => {
    const productHref = `/product/${cartItem.product.id}/${getSafeUrl(cartItem.product.name)}`;

    return (
        <div className="box-border relative flex flex-row gap-3 bg-white w-full rounded-2xl border border-neutral-200/70 shadow-sm p-3 overflow-hidden">
            <div className="flex justify-center items-start flex-shrink-0">
                {noLink ? (
                    <ProductThumb cartItem={cartItem} />
                ) : (
                    <Link href={productHref} prefetch={false}>
                        <ProductThumb cartItem={cartItem} />
                    </Link>
                )}
            </div>
            <div className="flex flex-col flex-1 min-w-0 gap-1.5">
                <div className="flex items-start justify-between gap-2">
                    {noLink ? (
                        <p className="text-neutral-900 font-semibold text-sm line-clamp-2 pr-4">
                            {getCombinedName(cartItem?.product, cartItem?.inventory?.variant_id)}
                        </p>
                    ) : (
                        <Link href={productHref} prefetch={false}>
                            <p className="text-neutral-900 font-semibold text-sm line-clamp-2 pr-4">
                                {getCombinedName(cartItem?.product, cartItem?.inventory?.variant_id)}
                            </p>
                        </Link>
                    )}
                    <button
                        title="REMOVE ITEM"
                        onClick={() => onRemove(cartItem)}
                        className="absolute top-3 right-3 flex-shrink-0 p-1.5 rounded-full text-error-400 hover:bg-error-100 cursor-pointer transition-colors"
                    >
                        <Trash2 size={15} />
                    </button>
                </div>
                {cartItem?.addon?.length > 0 && (
                    <p className="text-xs text-neutral-500 line-clamp-1">
                        <span className="font-semibold text-neutral-600">Addon: </span>
                        {cartItem?.addon?.map((x) => x?.addon?.product?.name)?.filter((x) => x != undefined).join(", ")}
                    </p>
                )}
                {sellerStore?.name?.length > 0 && (
                    <p className="text-xs text-neutral-500">
                        Seller:{' '}
                        {noLink ? (
                            <span className="text-neutral-600 font-medium">{sellerStore?.name}</span>
                        ) : (
                            <Link href={`/store/${sellerStore?.id}/${getSafeUrl(sellerStore?.name)}`} prefetch={false}>
                                <span className="text-neutral-600 font-medium">{sellerStore?.name || "N/A"}</span>
                            </Link>
                        )}
                    </p>
                )}
                {cartItem?.product?.stats?.rating_overall > 0 && (
                    <Star rating={cartItem?.product?.stats?.rating_overall} />
                )}
                <div className="flex items-center justify-between pt-1">
                    <p className="text-business-500 font-bold text-base">
                        {humanizeCurrency(parseFloat(PriceCalculation(cartItem)))}
                    </p>
                    <div className="w-28">
                        {cartItem.inventory?.variant_id != null ? (
                            <CartButtonV5
                                product={cartItem.product}
                                Isvariant={false}
                                inventory={cartItem.inventory}
                                store={store}
                            ></CartButtonV5>
                        ) : (
                            <CartButton
                                product={cartItem.product}
                                Isvariant={true}
                                inventory={cartItem.inventory}
                                mode={''}
                                store={store}
                            ></CartButton>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
